import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useGoogleLogin} from '@react-oauth/google';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';
import {
  addCompanyIntegration, beginMicrosoftConnect, deleteCompanyIntegration, getCompanyIntegrations, MicrosoftConnectUnavailableError,
} from '../settings/integrations.service';
import {CompanyIntegrationDto} from '../settings/dtos/integration.dto';
import {
  connectErrorText, ConnectResultParams, GmailIntegration, IntegrationInfo, IntegrationInfos, OutlookIntegration,
} from '../settings/dtos/integration.types';
import {Dialog, Hairline, Hint, OutlinePill} from './AccountKit';

/*
 * The business's email on the Account page (SQ-743): the company sends its
 * estimates and invoices from ONE mailbox, Gmail or Outlook (SQ-324), and
 * this is where it is connected now that Settings no longer has Connected
 * accounts. The card shows it as a row beside Google Calendar; the business
 * form hangs it off the email field. Both go through the same connections
 * Settings used: Gmail hands the page a Google auth code to post, Outlook is
 * the API's own OAuth callback, which brings the browser back to /account
 * with the result in the query (read once by useConnectReturn).
 */

/** Where Microsoft's sign-in sends the browser back to. */
const RETURN_PATH = '/account';

const isMailbox = (row: CompanyIntegrationDto, info: IntegrationInfo) =>
  row.integrationName?.toLowerCase() === info.name && row.integrationType?.toLowerCase() === info.type;

export interface MailConnection {
  /** Undefined while loading; null when the company has no mailbox connected. */
  connected: {row: CompanyIntegrationDto; info: IntegrationInfo} | null | undefined;
  busy: boolean;
  error?: string;
  connect: (info: IntegrationInfo) => void;
  disconnect: () => void;
}

/** The company's mailbox: which one is connected, and Connect / Disconnect for it. */
export const useMailConnection = (): MailConnection => {
  const navigate = useNavigate();
  const [connected, setConnected] = useState<MailConnection['connected']>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(() => getCompanyIntegrations(navigate)
    .then(list => {
      for (const info of IntegrationInfos.filter(i => i.isMail)) {
        const row = list.find(r => isMailbox(r, info));
        if (row) { setConnected({row, info}); return; }
      }
      setConnected(null);
    })
    .catch(e => {
      if (!isAuthRedirectError(e)) logError('Could not read the email connection', {}, e as Error);
      setConnected(null);
    }), [navigate]);
  useEffect(() => { void load(); }, [load]);

  const gmail = useGoogleLogin({
    flow: 'auth-code',
    scope: GmailIntegration.scopes.join(' '),
    onSuccess: async response => {
      try {
        await addCompanyIntegration(navigate, {
          integrationType: GmailIntegration.type, integrationName: GmailIntegration.name, authType: 'oauth2', authCode: response.code,
        });
        await load();
      } catch (e) {
        logError('Connecting Gmail failed', {}, e as Error);
        setError('Could not connect Gmail.');
      } finally {
        setBusy(false);
      }
    },
    // Backed out of Google's consent: nothing connected, nothing wrong.
    onError: () => setBusy(false),
    onNonOAuthError: () => setBusy(false),
  });

  const outlook = async () => {
    try {
      const authorizeUrl = await beginMicrosoftConnect(navigate, `${window.location.origin}${RETURN_PATH}`);
      // The same tab: Microsoft's consent page, the API's callback, then back here.
      window.location.assign(authorizeUrl);
    } catch (e) {
      if (e instanceof MicrosoftConnectUnavailableError) {
        setError(e.message);
      } else if (!isAuthRedirectError(e)) {
        logError('Starting the Microsoft connection failed', {}, e as Error);
        setError('Could not start connecting Outlook.');
      }
      setBusy(false);
    }
  };

  const connect = (info: IntegrationInfo) => {
    setBusy(true);
    setError(undefined);
    if (info.name === OutlookIntegration.name) void outlook();
    else gmail();
  };

  const disconnect = async () => {
    if (!connected) return;
    setBusy(true);
    setError(undefined);
    try {
      await deleteCompanyIntegration(navigate, connected.row.id);
      await load();
    } catch (e) {
      logError('Disconnecting the business email failed', {}, e as Error);
      setError(`Could not disconnect ${connected.info.displayName}.`);
    } finally {
      setBusy(false);
    }
  };

  return {connected, busy, error, connect, disconnect: () => void disconnect()};
};

/** "Sending from x@y.com (Gmail)", or what connecting does. */
const connectedLine = (c: NonNullable<MailConnection['connected']>) =>
  c.row.address ? `Sending from ${c.row.address} (${c.info.displayName})` : `Sending from your ${c.info.displayName} mailbox`;

/**
 * The Microsoft callback's result on the way back to the Account page: said
 * once, then taken off the URL so a reload or a shared link does not repeat it.
 */
export const useConnectReturn = (): string | undefined => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState<string>();
  useEffect(() => {
    const connected = searchParams.get(ConnectResultParams.connected);
    const error = searchParams.get(ConnectResultParams.error);
    if (!connected && !error) return;
    const name = connected ?? searchParams.get(ConnectResultParams.attempted) ?? '';
    const displayName = IntegrationInfos.find(i => i.name === name)?.displayName ?? 'The account';
    setMessage(connected ? `${displayName} is connected. Estimates and invoices now go out from it.` : connectErrorText(error!, displayName));
    const next = new URLSearchParams(searchParams);
    next.delete(ConnectResultParams.connected);
    next.delete(ConnectResultParams.attempted);
    next.delete(ConnectResultParams.error);
    setSearchParams(next, {replace: true});
  }, [searchParams, setSearchParams]);
  return message;
};

/** The card's row, beside Google Calendar: the mailbox's state, Connect or Disconnect. */
export const BusinessEmailRow = ({email}: {email: string}) => {
  const mail = useMailConnection();
  const [choosing, setChoosing] = useState(false);
  const c = mail.connected;
  return (
    <>
      <Hairline/>
      <div className="ac-connect">
        <div className="ac-grow">
          <div className="ac-connect-name">Business email</div>
          <div className="ac-sub">{c ? connectedLine(c) : 'Send estimates and invoices from your own address'}</div>
          {mail.error && <div className="ac-error ac-error--small">{mail.error}</div>}
        </div>
        <div className="ac-connect-side">
          {c !== undefined && <span className="ac-connect-status">{c ? 'Connected' : 'Not connected'}</span>}
          {mail.busy || c === undefined
            ? <span className="ac-spinner ac-spinner--small" role="progressbar" aria-label="Working on the business email"/>
            : <button className={c ? 'ac-connect-pill ac-connect-pill--off' : 'ac-connect-pill'}
                      aria-label={c ? `Disconnect ${c.info.displayName}` : 'Connect business email'}
                      onClick={() => c ? mail.disconnect() : setChoosing(true)}>
                {c ? 'Disconnect' : 'Connect'}
              </button>}
        </div>
      </div>
      {choosing && <ConnectEmailDialog email={email} onPick={info => { setChoosing(false); mail.connect(info); }}
                                       onClose={() => setChoosing(false)}/>}
    </>
  );
};

/** The business form's tab under the email field: the same connection, named against the address being typed. */
export const SendFromTab = ({email}: {email: string}) => {
  const mail = useMailConnection();
  const [choosing, setChoosing] = useState(false);
  const c = mail.connected;
  const status = c ? connectedLine(c) : email.trim() ? 'Not connected' : 'Add your email first';
  return (
    <div className="ac-send-from">
      <div className="ac-grow">
        <div className="ac-send-text">Send estimates and invoices from this email</div>
        <div className="ac-sub ac-sub--small">{c === undefined ? 'Checking…' : status}</div>
        {mail.error && <div className="ac-error ac-error--small">{mail.error}</div>}
      </div>
      {mail.busy || c === undefined
        ? <span className="ac-spinner ac-spinner--small" role="progressbar" aria-label="Working on the business email"/>
        : c
          ? <OutlinePill text="Disconnect" label={`Disconnect ${c.info.displayName}`} onClick={mail.disconnect}/>
          : <OutlinePill text="Connect" label="Connect business email" disabled={!email.trim()} onClick={() => setChoosing(true)}/>}
      {choosing && <ConnectEmailDialog email={email} onPick={info => { setChoosing(false); mail.connect(info); }}
                                       onClose={() => setChoosing(false)}/>}
    </div>
  );
};

const PROVIDERS: {key: string; label: string; detail: string; info?: IntegrationInfo}[] = [
  {key: 'google', label: 'Google', detail: 'Gmail or Google Workspace', info: GmailIntegration},
  {key: 'microsoft', label: 'Microsoft', detail: 'Outlook or Microsoft 365', info: OutlookIntegration},
  {key: 'other', label: 'Another provider', detail: 'Any other email service'},
];

/**
 * Android's "Send from your email": pick who the email is with. Google and
 * Microsoft start signing in at once; another provider cannot send yet, and
 * the dialog says so and stays open.
 */
export const ConnectEmailDialog = ({email, onPick, onClose}: {
  email: string; onPick: (info: IntegrationInfo) => void; onClose: () => void;
}) => {
  const [other, setOther] = useState(false);
  return (
    <Dialog title="Send from your email" onClose={onClose}>
      <Hint>
        Savaquan will send your estimates and invoices from {email.trim() || 'your email'}, so customers know they're from you and their
        replies come back to your inbox.
      </Hint>
      <div className="ac-dialog-question">Who is your email with?</div>
      {PROVIDERS.map(p => (
        <button key={p.key} className={other && !p.info ? 'ac-provider ac-provider--on' : 'ac-provider'}
                aria-pressed={other && !p.info}
                onClick={() => p.info ? onPick(p.info) : setOther(true)}>
          <b>{p.label}</b><span>{p.detail}</span>
        </button>
      ))}
      {other && <Hint>Savaquan can send from Google or Microsoft email for now. Other providers are coming.</Hint>}
      <button className="ac-text-button ac-dialog-close" onClick={onClose}>Close</button>
    </Dialog>
  );
};
